import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, Crosshair } from 'lucide-react'
import { siteData } from '../../data/siteData'

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.play().catch(() => {
        // Autoplay blocked, fallback handled by poster
      })
    }
  }, [])

  const scrollToAbout = () => {
    const el = document.getElementById('about')
    el?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToContact = () => {
    const el = document.getElementById('contact')
    el?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=1920&h=1080&fit=crop"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src="https://cdn.coverr.co/videos/coverr-military-soldiers-in-formation-3987/1080p.mp4"
            type="video/mp4"
          />
        </video>

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-hero-top" />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-military/10" />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(61,90,39,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(61,90,39,0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8"
        >
          <img
            src="/images/logo.png"
            alt="Strefa TOP"
            className="w-28 h-28 sm:w-36 sm:h-36 drop-shadow-2xl"
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="font-mono text-xs sm:text-sm text-sand tracking-[0.4em] uppercase mb-6"
        >
          ASG & Paintball · Tarnów Opolski
        </motion.p>

        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-center mb-4"
        >
          <h1 className="font-display text-shadow-hero leading-[0.9] tracking-wider">
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] text-bone">WEJDŹ DO</span>
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] text-military-light">STREFY.</span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-center mb-12"
        >
          <p className="font-display text-shadow-hero leading-[0.9] tracking-wider" aria-label="Wyjdź legendą">
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-bone/80">WYJDŹ</span>
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-sand">LEGENDĄ.</span>
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="flex flex-col sm:flex-row gap-4 items-center"
        >
          <button
            onClick={scrollToContact}
            className="group relative px-10 py-4 bg-military-light text-bone font-subheading font-bold text-base uppercase tracking-widest rounded-sm overflow-hidden transition-all duration-300 hover:bg-military-glow hover:shadow-xl hover:shadow-military-light/30 hover:scale-[1.04]"
          >
            <span className="relative z-10 flex items-center gap-2">
              <Crosshair size={18} />
              Zarezerwuj teraz
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-military-light to-military-glow opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          <button
            onClick={scrollToAbout}
            className="px-10 py-4 border border-bone/30 text-bone font-subheading font-semibold text-base uppercase tracking-widest rounded-sm transition-all duration-300 hover:bg-bone/10 hover:border-bone/60 hover:scale-[1.04]"
          >
            Dowiedz się więcej
          </button>
        </motion.div>

        {/* Phone */}
        <motion.a
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          href={`tel:${siteData.phone}`}
          className="mt-8 font-mono text-sm text-chrome hover:text-bone transition-colors tracking-wider"
        >
          {siteData.phone}
        </motion.a>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.button
          onClick={scrollToAbout}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="text-chrome hover:text-bone transition-colors"
          aria-label="Przewiń w dół"
        >
          <ChevronDown size={32} />
        </motion.button>
      </motion.div>

      {/* Corner Decorations */}
      <div className="absolute top-24 left-4 w-16 h-16 border-l-2 border-t-2 border-military/20 z-10" />
      <div className="absolute top-24 right-4 w-16 h-16 border-r-2 border-t-2 border-military/20 z-10" />
      <div className="absolute bottom-24 left-4 w-16 h-16 border-l-2 border-b-2 border-military/20 z-10" />
      <div className="absolute bottom-24 right-4 w-16 h-16 border-r-2 border-b-2 border-military/20 z-10" />
    </section>
  )
}
